import type { UnknownAction } from '@reduxjs/toolkit';
import h from 'hyperscript';

import swapViewsIcon from '@disy/cadenza-icons/illu-swap-views.svg';
import { isChromiumBrowser, isSafari } from '@disy/lib/browser';
import type { HexColor } from '@disy/lib/colors';
import { applyBg } from '@disy/lib/colors';
import { assert, requireNonNullable } from '@disy/lib/custom-error';
import { on } from '@disy/lib/event-util';
import { isEqual } from '@disy/lib/is-equal';
import type { Teardown } from '@disy/lib/teardown';
import { tearDown } from '@disy/lib/teardown';
import { getUrlParam } from '@disy/lib/url-param-util';
import '@disy/ui/alert.css';
import { icon } from '@disy/ui/icon';
import type { Settings } from '@disy/workbook.lib/settings';
import { getSetting, getSettingOrSettingDefault } from '@disy/workbook.lib/settings';

import type { WorldBoxDto } from 'cadenza/api-client/dto/world-box-dto';
import { getAddAttributeToViewPredictedResult } from 'cadenza/api-client/workbook-api';
import { Command } from 'cadenza/api-client/workbook-commands';
import { hasCapability } from 'cadenza/data-source-util';
import { isActiveWorksheetOnDemand } from 'cadenza/map/on-demand/on-demand-map.state';
import { getStore } from 'cadenza/store/cadenza-store';
import { observeStore } from 'cadenza/store/state-utils';
import type { PartialContext } from 'cadenza/ui/link/model/partial-context.gen';
import { dataTransfer } from 'cadenza/utils/datatransfer';
import type { ActiveAttributeId } from 'cadenza/workbook/api/active-attribute-id';
import { DataViewVisualizationKey, isMap } from 'cadenza/workbook/api/data-view-visualization-key';
import type { LayerId } from 'cadenza/workbook/api/layer-id';
import type { MapViewContextDto } from 'cadenza/workbook/api/map-view-context-dto';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import { ViewType } from 'cadenza/workbook/api/view-type';
import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';
import type { WorksheetId } from 'cadenza/workbook/api/worksheet-id';
import { DataView } from 'cadenza/workbook/data-view/data-view';
import { getAllAttributes } from 'cadenza/workbook/data-view/data-view-utils';
import { getUnreferencedDataViews, isUnreferencedLayer } from 'cadenza/workbook/map/workbook-layer-utils';
import { UNUSED_BUNDLE_NAME } from 'cadenza/workbook/model/attribute-bundle-name';
import type { AttributeId } from 'cadenza/workbook/model/attribute-id.gen';
import { CommonViewSettingsKey } from 'cadenza/workbook/model/common-view-settings-key.gen';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import { MapViewSettingsKey } from 'cadenza/workbook/model/map-view-settings-key.gen';
import { WorksheetLinearLayoutViewOrder } from 'cadenza/workbook/model/worksheet-linear-layout-view-order.gen';
import type { WorkbookNotification } from 'cadenza/workbook/notifications/workbook-notifications';
import { getIntegratedOverviewMapId } from 'cadenza/workbook/state/workbook-map-view-selectors.state';
import { combinationChartDiscardingOtherDataViewsConfirmation } from 'cadenza/workbook/view/chart/combination-chart-drag-and-drop-utils';
import { ScrollCover } from 'cadenza/workbook/view/scroll-cover/scroll-cover';
import { getMixedViewBackgroundColor, isViewConfigComplete } from 'cadenza/workbook/view/view-config-utils';
import type { EmbeddedContent } from 'cadenza/workbook/view/view-content/embedded-content';
import type { IncompleteContent } from 'cadenza/workbook/view/view-content/incomplete-content';
import type { ViewContent } from 'cadenza/workbook/view/view-content/view-content';
import { createContent } from 'cadenza/workbook/view/view-content/view-content-provider';
import { ViewCover } from 'cadenza/workbook/view/view-cover/view-cover';
import { ContentViewToolbar } from 'cadenza/workbook/view/view-header/content-view-toolbar';
import type { IViewToolbar } from 'cadenza/workbook/view/view-header/i-view-toolbar';
import { ViewNotificationBar } from 'cadenza/workbook/view/view-header/view-notification-bar';
import { WorksheetViewToolbar } from 'cadenza/workbook/view/view-header/worksheet-view-toolbar';
import { createViewNotificationButton } from 'cadenza/workbook/view/view-notification-button/view-notification-button';
import { isLobAttributeWithoutRelation } from 'cadenza/workbook/workbook-attribute-util';
import { executeCommand } from 'cadenza/workbook/workbook-core-reducers.state';
import {
  getActiveViewId,
  getActiveWorksheet,
  getActiveWorksheetId,
  getAttributeById,
  getDataSourceByObjecttypeId,
  getDataViewsAffectedBySelectionChange,
  getInvalidViewsOrDataViews,
  getNotificationsForView,
  getViewById,
  getVisualizationKeyByDataViewId,
  getWorkbookNameAndHash,
  getWorksheetByViewId,
  isCombinationChart,
  isDetailView,
  isIntegratedView,
  isMaximizedView,
  isViewInvalid,
  layerById,
} from 'cadenza/workbook/workbook-core-selectors.state';
import { isWorkbookFeatureAvailable } from 'cadenza/workbook/workbook-features';
import {
  areViewsOrderedAutomatically,
  getViewOrderSetting,
} from 'cadenza/workbook/workbook-panel/settings/worksheet/linear-layout-order-utils';
import { activateView, deactivateView, unmarkViewAsDirty } from 'cadenza/workbook/workbook.state';
import { isLinearLayout, worksheetLayout } from 'cadenza/workbook/worksheet-layout';
import type { GridAreaDto } from 'cadenza/workbook/worksheet/dto/grid-area-dto';
import {
  GRID_SIZE,
  setGridRowDependentHeight,
  toCssGridLayout,
} from 'cadenza/workbook/worksheet/worksheet-grid/worksheet-layouting-util';

import './view.css';

const COMPONENT_NAME = 'd-worksheet-view';
const VIEW_TYPES_WITH_TRANSPARENT_TOOLBAR: ViewType[] = [ViewType.IMAGE];

const IS_MAXIMIZED_CLASS = 'is-maximized';
const IS_DETAIL_CLASS = 'is-detail-view';
const IS_LINEAR_LAYOUT_CLASS = 'is-linear-layout';
const IS_INTEGRATED_CLASS = 'is-integrated';
const IS_ACTIVE_CLASS = 'is-active';

const DROP_ALLOWED_CLASS = 'drop-allowed';
const DROP_DENIED_CLASS = 'drop-denied';

interface ViewRenderOptions {
  /**
   * This is set to <code>true</code> if the user demanded a reload in an on-demand worksheet.
   */
  reloadDemanded?: boolean;
  /** Whether the content should be rendered. */
  canRenderContent?: boolean;
  /** Whether the filter condtions are valid. */
  validConditions?: boolean;
  /** IDs of views or data views that need to be invalidated. */
  invalidViewsOrDataViews?: (ViewId | DataViewId)[];
  /** IDs of data views that are affected by a selection. */
  dataViewsAffectedBySelectionChange?: DataViewId[];
}

export class View extends HTMLElement {
  readonly #viewCover;
  readonly #viewNotificationBar;

  readonly #worksheetId: WorksheetId;
  #type!: ViewType;
  #settings!: Settings;
  #layout?: GridAreaDto;
  #dataViews: DataView[] = [];
  #mapMaxExtent?: WorldBoxDto;
  #toolbar?: IViewToolbar & HTMLElement;

  #teardowns: Teardown[] = [];
  #content!: EmbeddedContent | ViewContent | IncompleteContent;

  constructor(id: ViewId) {
    super();
    this.#worksheetId = getActiveWorksheetId(getStore().getState());
    this.dataset.id = id;
    this.#updateFromStore();
    this.#viewCover = new ViewCover(this);
    this.#viewNotificationBar = new ViewNotificationBar(id);
    this.classList.add(COMPONENT_NAME, 'd-content-font');

    this.append(this.#viewNotificationBar);
    this.append(this.#viewCover);
    this.append(new ScrollCover(this));
    this.append(createSwapOverlay());
    this.append(createViewNotificationButton(id));
  }

  connectedCallback() {
    const isIntegrated = isIntegratedView(getStore().getState(), this.viewId);
    this.classList.toggle(IS_INTEGRATED_CLASS, isIntegrated);
    this.classList.toggle('drop-area', !isIntegrated);
    this.classList.toggle('d-hover-context', !isIntegrated);
    this.classList.toggle('d-resizable', !isIntegrated);
    this.setAttribute('tabindex', '0');
    this.#initialize(isIntegrated);
  }

  disconnectedCallback() {
    this.#teardowns = tearDown(this.#teardowns);
  }

  /**
   * @param reloadDemanded Set to <code>true</code> when the user demanded
   * a reload in an on-demand worksheet. This means that all data views will
   * be reloaded, whether they are on-demand or not.
   */
  reload(reloadDemanded?: boolean) {
    const invalidViewsOrDataViews = getInvalidViewsOrDataViews(getStore().getState());
    const dataViewsAffectedBySelectionChange = getDataViewsAffectedBySelectionChange(getStore().getState());

    const validConditions = getActiveWorksheet(getStore().getState()).requiredConditionsValid;
    const canRenderContent = validConditions || !this.#dataViews.length; // If a view has not dataview, it does not depend on conditions. It has to be rendered always, because it will not be updated on condition change.
    this.#render({
      reloadDemanded,
      canRenderContent,
      validConditions,
      invalidViewsOrDataViews,
      dataViewsAffectedBySelectionChange,
    });

    /* We should not have such manual unmarking and replace it with a counter (or similar):
     * When the counter changes, the Redux sees it and makes the UI update. Has it changed while already updating (e.g.
     * when ajax requests for updated data are pending), the UI triggers a new update and stops the pending requests.
     * @see TechnicalDebt CADENZA-38372
     */
    dispatchDeferred(unmarkViewAsDirty(this.viewId));
    // in case the map view has an integrated overview map,
    // the overview map must also be unmarked as dirty
    // as otherwise the correct dirty state of the worksheet
    // does not change correctly for worksheet on demand
    const overviewMapId = getIntegratedOverviewMapId(getStore().getState(), this.viewId);
    if (overviewMapId) {
      dispatchDeferred(unmarkViewAsDirty(overviewMapId));
    }
  }

  #reloadDirtyView(isReloadRequiredByConfigChange: boolean) {
    // In on demand mode load only if shown in a popup dialog
    if (!isActiveWorksheetOnDemand(getStore().getState()) && isViewInvalid(getStore().getState(), this.viewId)) {
      this.reload(isReloadRequiredByConfigChange);
    }
  }

  setDropAllowedForDraggedAttribute() {
    const attributeId = getAttributeIdFromDropType();
    if (!attributeId) {
      this.dropAllowed = false;
      return;
    }
    const attribute = getAttributeById(getStore().getState(), attributeId);
    const dropAllowed =
      // getAttributeById does not include active attributes from filters in analysis context (e.g. user drags filter over the view)
      attribute &&
      // attribute not broken
      attribute.status === 'OK' &&
      attributeModificationUiAvailableOrAttributeFromTheSameView(attributeId, this.viewId) &&
      (this._isCreateNewMapLayerOnAttributeDrop(attributeId) || this.getDataViewThatAcceptsAttribute(attributeId)) &&
      !isLobAttributeWithoutRelation(attribute);
    this.dropAllowed = Boolean(dropAllowed);
  }

  clearDropAllowedForDraggedAttribute() {
    this.dropAllowed = undefined;
  }

  async handleAttributeDrop(event: Event) {
    /*
     * setting dropAllowed should happen on 'dragenter' event, but because IE11 sometimes does NOT
     * catch it, it's additionally set here
     */
    this.setDropAllowedForDraggedAttribute();

    if (this.dropAllowed) {
      event.preventDefault();
      const attributeId = getAttributeIdFromDropType()!;
      await this.addAttribute(attributeId);
      getStore().dispatch(activateView(this.viewId));
    }

    /*
     * clearing dropAllowed should happen on 'dragleave' event, but because IE11 sometimes does NOT
     * catch it, clearing is additionally called here
     */
    this.clearDropAllowedForDraggedAttribute();
  }

  /**
   * Adds given attribute id to the first dataview that accepts the objecttype of the given attributeId
   *
   * @param attributeId - The ID of the Attribute to add to this view
   */
  async addAttribute(attributeId: AttributeId) {
    if (this._isCreateNewMapLayerOnAttributeDrop(attributeId)) {
      return getStore().dispatch(
        executeCommand(
          Command.ADD_ATTRIBUTE({
            viewId: this.viewId,
            attributeId,
            index: 0,
          }),
        ),
      );
    } else {
      if (isCombinationChart(getStore().getState(), this.viewId)) {
        return this.addAttributeToCombinationChart(attributeId);
      }
      return this.doAddAttributeToDataView(
        requireNonNullable(
          this.getDataViewThatAcceptsAttribute(attributeId),
          `no DataView found accepting ${attributeId}`,
        ),
        attributeId,
      );
    }
  }

  async addAttributeToCombinationChart(attributeId: AttributeId) {
    const { dataViewId, recommendedVisualization } = await getAddAttributeToViewPredictedResult(
      getWorkbookNameAndHash(getStore().getState()),
      this.viewId,
      attributeId,
    );
    return recommendedVisualization !== getVisualizationKeyByDataViewId(getStore().getState(), dataViewId) ?
        combinationChartDiscardingOtherDataViewsConfirmation(this.#getRequiredDataView(dataViewId), attributeId)
      : this.doAddAttributeToDataView(this.#getRequiredDataView(dataViewId), attributeId);
  }

  async doAddAttributeToDataView(dataView: DataView, attributeId: AttributeId) {
    await dataView.addAttribute(attributeId);
  }

  /**
   * Updates the view to the new data state.
   * Only model changes are allowed. Don't update the view in this method call.
   *
   */
  #updateFromStore() {
    const { type, settings, layout, dataViews, mapViewContext } = getViewClone(this.viewId);
    if (type !== ViewType.MAP) {
      this.toggleToastMessages(true);
    }
    const isReloadRequiredByConfigChange =
      this.#type !== type || this.#getIsReloadRequiredDueToSettingsChange(this.#settings, settings);
    this.#settings = settings;
    this.setLayoutOnClient(layout);
    if (dataViews) {
      this.updateDataViews(dataViews);
    }
    const isReloadRequiredByMapMaxExtent =
      type === ViewType.MAP && this.#getIsReloadRequiredDueToMaxExtentChange(mapViewContext);
    if (mapViewContext) {
      this.#mapMaxExtent = mapViewContext?.maxExtent;
    }
    return isReloadRequiredByConfigChange || isReloadRequiredByMapMaxExtent;
  }

  // enables/disables showing toast messages
  toggleToastMessages(enable: boolean) {
    this.classList.toggle('no-toast-msg', !enable);
  }

  #getIsReloadRequiredDueToSettingsChange(oldSettings: Settings, newSettings: Settings) {
    return (
      settingChanged(oldSettings, newSettings, MapViewSettingsKey.toPersistenceKey('TIMELINE_3D_ENABLED')) ||
      settingChanged(oldSettings, newSettings, MapViewSettingsKey.toPersistenceKey('SCALE_RANGE_MIN')) ||
      settingChanged(oldSettings, newSettings, MapViewSettingsKey.toPersistenceKey('SCALE_RANGE_MAX'))
    );
  }

  #getIsReloadRequiredDueToMaxExtentChange(mapViewContext?: MapViewContextDto) {
    return !isEqual(this.#mapMaxExtent, mapViewContext?.maxExtent);
  }

  /**
   * Updates the view. No model changes should happen here.
   * This method won't be triggered when user is interacting with the map.
   */
  // eslint-disable-next-line no-restricted-syntax
  #render({
    reloadDemanded,
    canRenderContent,
    validConditions,
    invalidViewsOrDataViews,
    dataViewsAffectedBySelectionChange,
  }: ViewRenderOptions = {}) {
    this.updateToolbar();

    // performs checks to only actually render the view cover when certain conditions apply, like missing data
    const viewCover = this.#viewCover;
    const currentContent = this.#content;
    const notifications = getNotificationsForView(getStore().getState(), this.viewId);
    this.#updateViewNotifications(notifications);
    const renderOption = {
      reloadDemanded,
      invalidViewsOrDataViews,
      dataViewsAffectedBySelectionChange,
    };
    if (
      !currentContent ||
      !currentContent.canUpdateTo(this.#type) ||
      !isViewConfigComplete(this.#type, this.#dataViews) ||
      reloadDemanded
    ) {
      const newContent = createContent(this);
      if (currentContent) {
        currentContent.replaceWith(newContent);
      } else {
        viewCover.after(newContent);
      }

      this.#content = newContent;
    }
    if (canRenderContent) {
      this.#content.render(renderOption);
    } else if (!validConditions) {
      // CADENZA-37786, CADENZA-37789, in case of conditions invalid, we need to deactivate the view, otherwise,
      // the invalid conditions overlay is not visible
      dispatchDeferred(deactivateView());
    }

    this.#assignVisualizationKeyForTesting();
  }

  get content() {
    return this.#content;
  }

  get dropAllowed() {
    return this.classList.contains(DROP_ALLOWED_CLASS);
  }

  set dropAllowed(value: boolean | undefined) {
    if (value === undefined) {
      this.classList.remove(DROP_ALLOWED_CLASS, DROP_DENIED_CLASS);
    } else {
      const dropAllowed = Boolean(value);
      this.classList.toggle(DROP_ALLOWED_CLASS, dropAllowed);
      this.classList.toggle(DROP_DENIED_CLASS, !dropAllowed);
    }
  }

  /**
   * Returns the first data view that supports the given attribute or undefined if there is none.
   *
   * @param attributeId - The attributeId that should be reachable by the dataview to find.
   * @return The first DataView that is reachable by the given objecttype or undefined if there is none.
   */
  getDataViewThatAcceptsAttribute(attributeId: AttributeId) {
    const attribute = getAttributeById(getStore().getState(), attributeId);

    // Check if, for map view, we have at least one layer that is not a reference layer when the attribute has no geometry relation.
    if (this.#type === 'MAP' && !attribute.hasGeometryRelation) {
      const dataViewHasNonReferenceLayer = this.#dataViews.some((dataView) => {
        const layer = layerById(getStore().getState(), this.viewId, dataView.dataViewId);
        return isUnreferencedLayer(layer, this.viewId);
      });
      if (!dataViewHasNonReferenceLayer) {
        return;
      }
    }

    return getUnreferencedDataViews(this).find((dataView) => {
      if (dataView.visualizationKey === DataViewVisualizationKey.REPORT.JASPER_REPORT) {
        const dataSource = getDataSourceByObjecttypeId(getStore().getState(), attribute.objecttypeId);
        return hasCapability(dataSource, 'JASPER_REPORTS');
      }
      const addingNonGeometryToEmptyMapDataView =
        isMap(dataView.visualizationKey) &&
        !dataView.mainObjecttype &&
        !(attribute.hasGeometryRelation || attribute.type === 'GEOMETRY');
      if (this.type === 'CHART' && isAttributeInUnused(dataView, attributeId)) {
        return;
      }
      if (dataView.isAttributeAccepted(attribute) && !addingNonGeometryToEmptyMapDataView) {
        return dataView.acceptsAttribute(attribute);
      }
      return isMap(dataView.visualizationKey) && (attribute.hasGeometryRelation || attribute.type === 'GEOMETRY');
    })!;
  }

  /**
   * returns true if the objecttype of the given attribute is reachable by any dataview from this view
   *
   * @return true if the object type of the given attribute is reachable by any data view
   */
  isAccepted(attribute: WorkbookAttributeDto) {
    return (
      this.#dataViews.some((dataView) => dataView.isAttributeAccepted(attribute)) ||
      this._isCreateNewMapLayerOnAttributeDrop(attribute.id)
    );
  }

  _isCreateNewMapLayerOnAttributeDrop(attributeId: AttributeId | ActiveAttributeId) {
    const attribute = getAttributeById(getStore().getState(), attributeId);
    return this.#type === 'MAP' && (attribute.type === 'GEOMETRY' || attribute.hasGeometryRelation);
  }

  /**
   * Returns the view's ViewId.
   *
   * Getter is named viewId to not collide with native Element#id property
   *
   * @return the ViewId of this view (not its element id)
   */
  get viewId(): ViewId {
    return this.dataset.id! as ViewId;
  }

  get type() {
    return this.#type;
  }

  get name() {
    return getViewById(getStore().getState(), this.viewId).name;
  }

  get defaultName() {
    return getViewById(getStore().getState(), this.viewId).defaultName;
  }

  get dataViews() {
    return this.#dataViews;
  }

  get dataViewIds() {
    return this.#dataViews.map(({ dataViewId }) => dataViewId);
  }

  /**
   * Return DataView by id
   */
  getDataView(dataViewId: DataViewId): DataView | undefined {
    return this.#dataViews.find(({ dataViewId: id }) => id === dataViewId);
  }

  #getRequiredDataView(dataViewId: DataViewId) {
    return requireNonNullable(this.getDataView(dataViewId));
  }

  updateDataViews(dataViewIds: DataViewId[] = []) {
    this.#dataViews = this.#dataViews.filter(({ dataViewId }) => dataViewIds.includes(dataViewId));
    dataViewIds.forEach((dataViewId) => {
      if (!this.getDataView(dataViewId)) {
        this.#dataViews.push(new DataView(dataViewId, this.viewId));
      }
    });
  }

  updateToolbar() {
    if (this.#hasToolbar()) {
      this.#toolbar?.update();
    }
  }

  /** Returns all attributes of all dataViews of this view in one array. */
  get allAttributes() {
    return this.#dataViews.flatMap((dataView) => dataView.getAllAttributes());
  }

  _createToolbar() {
    return VIEW_TYPES_WITH_TRANSPARENT_TOOLBAR.includes(this.#type) ?
        new ContentViewToolbar(this)
      : new WorksheetViewToolbar(this);
  }

  get grid() {
    return this.closest('.d-worksheet-grid');
  }

  /** Moves the view to the specified location on the worksheet grid and informs the server about it. */
  setLayoutOnClientAndServer(layout: GridAreaDto) {
    this.setLayoutOnClient(layout);

    getStore().dispatch(
      executeCommand(
        Command.SET_VIEW_LAYOUT({
          viewId: this.viewId,
          layout,
        }),
      ),
    );
  }

  setLayoutOnClient(layout?: GridAreaDto) {
    if (layout) {
      assert(validateGridArea(layout), 'Grid area must be an array of coordinates, defining a rect');
      // https://developer.mozilla.org/en-US/docs/Web/CSS/grid-area
      this.style.gridArea = toCssGridLayout(layout).join('/');
    }
    this.#layout = layout;
  }

  get layout() {
    return this.#layout;
  }

  get settings() {
    return this.#settings;
  }

  get viewTitle() {
    // ! – because it's guaranteed to be there
    return this.querySelector(`.${COMPONENT_NAME}--title`)!.textContent;
  }

  // Doesn't overwrite existing settings entries with different keys than the given new settings
  addSettings(settings: Settings) {
    for (const [key, value] of Object.entries(settings)) {
      this.#settings[key] = value;
    }
    return this._executeChangeViewSettingsCommand();
  }
  _executeChangeViewSettingsCommand() {
    return getStore().dispatch(
      executeCommand(
        Command.CHANGE_VIEW_SETTINGS({
          viewId: this.viewId,
          settings: this.#settings,
        }),
      ),
    );
  }

  updateLayerSettings(layerId: LayerId, settings: Settings) {
    return getStore().dispatch(
      executeCommand(
        Command.CHANGE_LAYER_SETTINGS({
          viewId: this.viewId,
          layerId,
          settings,
        }),
      ),
    );
  }

  removeLayer(layerIdToRemove: LayerId, dataViewIdToRemove?: DataViewId) {
    this.#dataViews = this.#dataViews.filter(({ dataViewId }) => dataViewId !== dataViewIdToRemove);
    getStore().dispatch(
      executeCommand(
        Command.REMOVE_LAYER({
          viewId: this.viewId,
          layerId: layerIdToRemove,
        }),
      ),
    );
  }

  #fixMouseWheelScrolling() {
    if (isSafari) {
      this.classList.add('safari');
    } else if (isChromiumBrowser) {
      this.classList.add('chromium');
    }
  }

  _setOpaqueBackground(mixedBackgroundColor: HexColor) {
    this.style.backgroundColor = mixedBackgroundColor;
    applyBg(this, mixedBackgroundColor);
  }

  #initialize(isViewIntegrated: boolean) {
    this.toggleToastMessages(false);
    setViewCSSStyleOrder(this.#worksheetId, this);
    setViewCSSStyleVisibility(this);
    this.#fixMouseWheelScrolling();
    this.#observeBackgroundColor();
    this.#observeViewType();
    this.#observeViewInvalid(); // This must be before #observeUpdateFromStore else 2d/3d switches might get lost.
    this.#observeUpdateFromStore();
    this.#observeLinearLayoutOrder();
    this.#observeViewNotifications();
    if (!isViewIntegrated) {
      this.#initializeDragDropEvents();
      this.#observeActiveView();
      this.#observeInteractionWithView();
      this.#observeMaximizeView();
      this.#observeDetailView();
    } else {
      this.classList.remove(IS_ACTIVE_CLASS);
    }
  }

  #initializeDragDropEvents() {
    this.#teardowns.push(
      on(this, 'dragover', (event) => {
        if (this.dropAllowed) {
          event.preventDefault();
        }
      }),
      /*
       * handleAttributeDrop() was used in 'drop' event callback, but IE11 sometimes does NOT catch
       * 'drop' event, so different event must be used - in this case onEnd from sortable-list.js.
       * onEnd uses dragend, which next to dragstart, is an event that always works on IE11.
       */
      on(this, 'dragleave', () => {
        this.clearDropAllowedForDraggedAttribute();
      }),
      on(this, 'dragenter', () => {
        /* With the timeout, dragenter should be applied last and have priority over dragleave.
         Previously we had race conditions and the new value was overwritten with undefined. */
        if (!isLinearLayout()) {
          setTimeout(this.setDropAllowedForDraggedAttribute.bind(this), 0);
        }
      }),
    );
  }

  #observeViewInvalid() {
    this.#teardowns.push(
      observeStore(
        getStore(),
        (state) => isViewInvalid(state, this.viewId),
        () => {
          // Note: this is fired immediately when the workbook state is populated and is
          // responsible for the initial rendering of the view.
          this.#reloadDirtyView(this.#updateFromStore());
        },
      ),
    );
  }

  #observeUpdateFromStore() {
    this.#teardowns.push(
      observeStore(
        getStore(),
        (state) => getViewById(state, this.viewId),
        () => {
          this.#updateFromStore();
        },
        true,
      ),
    );
  }

  #observeBackgroundColor() {
    this.#teardowns.push(
      observeStore(
        getStore(),
        (state) => getMixedViewBackgroundColor(state, this.viewId),
        (backgroundColor) => this._setOpaqueBackground(backgroundColor),
        true,
      ),
    );
  }

  #observeViewType() {
    this.#teardowns.push(
      observeStore(
        getStore(),
        (state) => getViewById(state, this.viewId).type,
        (type: ViewType) => {
          this.#setViewType(type);
          this.#createOrRemoveToolbar();
          this.dataset.testid = `worksheet-view-of-type-${this.#type.toLowerCase()}`;
        },
      ),
    );
  }

  #observeLinearLayoutOrder() {
    this.#teardowns.push(
      observeStore(
        getStore(),
        () => getViewOrderMode(this),
        () => setViewCSSStyleOrder(this.#worksheetId, this),
      ),
      observeStore(
        getStore(),
        () => getLinearLayoutOrderSetting(this),
        () => setViewCSSStyleOrder(this.#worksheetId, this),
      ),
      observeStore(
        getStore(),
        () => getLinearLayoutVisibleSetting(this),
        () => setViewCSSStyleVisibility(this),
      ),
      on(worksheetLayout, 'change', () => {
        setViewCSSStyleVisibility(this);
      }),
    );
  }

  #setViewType(type: ViewType) {
    if (this.#type) {
      this.classList.remove(getTypeClassName(this.#type)); // Remove old class
    }
    this.#type = type;
    this.classList.add(getTypeClassName(this.#type)); // Add new class
  }

  #createOrRemoveToolbar() {
    if (this.#hasToolbar()) {
      if (this.#toolbar && this.contains(this.#toolbar)) {
        this.#toolbar.remove();
        this.#toolbar = undefined;
      }
      this.#toolbar = this._createToolbar();
      this.prepend(this.#toolbar);
    } else if (this.#toolbar) {
      this.#toolbar.remove();
      this.#toolbar = undefined;
    }
  }

  #hasToolbar() {
    return (
      !isIntegratedView(getStore().getState(), this.viewId) &&
      isWorkbookFeatureAvailable(getStore().getState(), 'workbook-view-titlebar')
    );
  }

  #observeInteractionWithView() {
    this.#teardowns.push(
      on(this, ['focusin', 'click', 'mousedown', 'touchstart', 'wheel'], () => {
        getStore().dispatch(activateView(this.viewId));
      }),
    );
  }

  #observeActiveView() {
    this.#teardowns.push(
      observeStore(getStore(), getActiveViewId, (viewId) => {
        const active = viewId === this.viewId;
        this.classList.toggle(IS_ACTIVE_CLASS, active);
        if (active && !this.contains(document.activeElement)) {
          this.focus({ preventScroll: true });
        }
      }),
    );
  }

  #observeMaximizeView() {
    this.#teardowns.push(
      observeStore(
        getStore(),
        (state) => isMaximizedView(state, this.viewId),
        (isMaximized) => {
          this.classList.toggle(IS_MAXIMIZED_CLASS, isMaximized);
        },
      ),
    );
  }

  #observeDetailView() {
    this.#teardowns.push(
      observeStore(
        getStore(),
        (state) => isDetailView(state, this.viewId),
        (isDetail) => {
          const partialContext = getUrlParam<PartialContext>('partialContext');
          if (!isLinearLayout() || partialContext === 'viewEmbedding' || partialContext === 'viewPopup') {
            return;
          }
          this.classList.toggle(IS_DETAIL_CLASS, isDetail);
          this.classList.toggle(IS_LINEAR_LAYOUT_CLASS, !isDetail);
          if (isDetail) {
            this.style.height = '';
            // Provide the state workbook-store independently
            document.documentElement.dataset.hasDetailView = 'true';
          } else {
            setGridRowDependentHeight(this);
            document.documentElement.dataset.hasDetailView = 'false';
          }
        },
      ),
    );
  }

  #observeViewNotifications() {
    this.#teardowns.push(
      observeStore(
        getStore(),
        (state) => getNotificationsForView(state, this.viewId),
        (newNotifications) => this.#updateViewNotifications(newNotifications),
        true,
      ),
    );
  }

  #updateViewNotifications(notifications: WorkbookNotification[]) {
    this.#viewCover.render();
    this.#viewNotificationBar.update(notifications);
    if (!this.#viewCover.hidden) {
      // if the view cover is visible, the notification bar is redundant, so it's hidden
      this.#viewNotificationBar.hidden = true;
    }
  }

  // In E2E test, currently there is no way to determine data view visualization. So we use attrbitue to expose the visualization
  // key so that test can determine which visualization has view changed to.
  #assignVisualizationKeyForTesting() {
    this.dataset.testVisualizationKey = this.#dataViews.map((dv) => dv.visualizationKey).join(',');
  }
}

customElements.define(COMPONENT_NAME, View);

function validateGridArea(area: GridAreaDto) {
  if (area?.length === 4 && area.every((value) => value >= 0 && value < GRID_SIZE)) {
    const [rowStart, columnStart, rowEnd, columnEnd] = area;
    return rowStart <= rowEnd && columnStart <= columnEnd;
  }
  return false;
}

function getAttributeIdFromDropType() {
  const attributeId = dataTransfer<{ attributeId: AttributeId }>();
  return attributeId ? attributeId.attributeId : undefined;
}

function getViewClone(id: ViewId) {
  // Cloning, because the properties of view can be mutated
  return structuredClone(getViewById(getStore().getState(), id));
}

function isAttributeInUnused(dataView: DataView, attributeId: AttributeId) {
  const bundles = dataView.getAllAttributeBundles();
  return bundles[UNUSED_BUNDLE_NAME]!.some((attr) => attr.id === attributeId);
}

function getTypeClassName(type: ViewType) {
  return `view-type--${type.toLowerCase()}`;
}

function settingChanged(oldSettings: Settings, newSettings: Settings, persistenceKey: string) {
  return (
    getSetting({ settings: oldSettings }, persistenceKey) !== getSetting({ settings: newSettings }, persistenceKey)
  );
}

function createSwapOverlay() {
  // When a view may be swapped with another view's position, the swap overlay indicates the
  // possible swap to the user.
  return h('.d-view-swap-overlay', icon(swapViewsIcon));
}

function setViewCSSStyleOrder(worksheetId: WorksheetId, view: View) {
  if (!areViewsOrderedAutomatically(worksheetId)) {
    const linearLayoutOrder = getLinearLayoutOrderSetting(view);
    if (linearLayoutOrder !== '') {
      view.style.order = linearLayoutOrder;
    }
  } else {
    view.style.order = '';
  }
}

function setViewCSSStyleVisibility(view: View) {
  const linearLayoutIsUsed = isLinearLayout();
  const linearLayoutVisible = getLinearLayoutVisibleSetting(view);
  view.classList.toggle('hidden', linearLayoutIsUsed && !linearLayoutVisible);
}

function getViewOrderMode(view: View) {
  const worksheet = getWorksheetByViewId(getStore().getState(), view.viewId);
  return worksheet ? getViewOrderSetting(worksheet) : WorksheetLinearLayoutViewOrder.toPersistenceKey('AUTO');
}

function getLinearLayoutOrderSetting(view: View) {
  return getSetting<string>(view, CommonViewSettingsKey.toPersistenceKey('LINEAR_LAYOUT_ORDER'), '');
}

function getLinearLayoutVisibleSetting(view: View) {
  return getSettingOrSettingDefault<boolean>(view, CommonViewSettingsKey.toPersistenceKey('LINEAR_LAYOUT_VISIBLE'));
}

// CADENZA-34009
function attributeModificationUiAvailableOrAttributeFromTheSameView(attributeId: AttributeId, viewId: ViewId) {
  return (
    (isWorkbookFeatureAvailable(getStore().getState(), 'workbook-data-browser') &&
      isWorkbookFeatureAvailable(getStore().getState(), 'workbook-data-view-content')) ||
    attributeFromTheSameView(attributeId, viewId)
  );
}

function attributeFromTheSameView(attributeId: AttributeId, viewId: ViewId) {
  const view = getViewById(getStore().getState(), viewId);
  const workbookAttributeIds = view.dataViews
    .flatMap((dataViewId) => getAllAttributes(dataViewId))
    .map((attr) => attr.id);
  return workbookAttributeIds.some((id) => id === attributeId);
}

/**
 * Dispatch in another microtask to not dispatch actions in observeStore callbacks (assuming this function is called in
 * such a callback). Otherwise, we may end up in an endless loop or with inconsistent UI updates. Redux would handle
 * such actions immediately while still not having invoked callbacks of the following listeners of the last change we are
 * currently resolving.
 * Changes triggered this way need to be done in a reducer/middleware, so it gets handled even before landing here.
 * @see TechnicalDebt CADENZA-39941
 */
function dispatchDeferred(action: UnknownAction) {
  queueMicrotask(() => getStore().dispatch(action));
}
